import { useEffect, useState, useCallback } from "react";
import { ChevronRight, ListOrdered, Search, Eye, AlertTriangle } from "lucide-react";
import api from "@/lib/crmApi";
import { Loader, Empty } from "@/crm/ui";
import { cn } from "@/lib/utils";
import { CALLER_META, RESULT_META, CARD, DOT, PILL, THEAD, TR, inputCls, fmtDate } from "./shared";

const fmtDur = (s) => {
  if (s == null) return null;
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")}`;
};

// « Sonnerie 8s · Durée 4 min 12 · Répondu par Julien Marchand »
const summaryOf = (c) => [
  `Sonnerie ${c.ring_duration != null ? `${c.ring_duration}s` : "—"}`,
  `Durée ${fmtDur(c.call_duration) || "—"}`,
  c.answered_by_name ? `Répondu par ${c.answered_by_name}` : null,
].filter(Boolean).join(" · ");

export default function CallJournal() {
  const [calls, setCalls] = useState(null);
  const [search, setSearch] = useState("");
  const [callerType, setCallerType] = useState("");
  const [result, setResult] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (callerType) params.caller_type = callerType;
    if (result) params.result = result;
    api.get("/telephony/calls", { params }).then(({ data }) => setCalls(data)).catch(() => setCalls([]));
  }, [search, callerType, result]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  if (calls === null) return <Loader />;

  const toggle = (id) => setOpen(open === id ? null : id);

  return (
    <div data-testid="telephony-journal">
      {/* Filtres */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3.5">
        <div className={cn(CARD, "flex-1 flex items-center gap-2.5 rounded-xl px-3 py-2.5")}>
          <Search size={16} className="text-slate-400 shrink-0" />
          <input data-testid="journal-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un numéro ou une entreprise…"
            className="flex-1 min-w-0 bg-transparent text-sm outline-none" />
        </div>
        <select data-testid="journal-filter-type" value={callerType} onChange={(e) => setCallerType(e.target.value)}
          className={cn(inputCls, "sm:w-48")}>
          <option value="">Tous les appelants</option>
          {Object.entries(CALLER_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select data-testid="journal-filter-result" value={result} onChange={(e) => setResult(e.target.value)}
          className={cn(inputCls, "sm:w-48")}>
          <option value="">Tous les résultats</option>
          {Object.entries(RESULT_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {calls.length === 0 ? (
        <div className={CARD}><Empty label="Aucun appel journalisé" icon={ListOrdered} /></div>
      ) : (
        <>
          {/* Tableau en lg+ */}
          <div className={cn(CARD, "overflow-hidden hidden lg:block")}>
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className={THEAD}>
                  <th className="px-5 py-2.5 text-left">Numéro</th>
                  <th className="px-2 py-2.5 text-left w-[168px]">Appelant</th>
                  <th className="px-2 py-2.5 text-left">Entreprise</th>
                  <th className="px-2 py-2.5 text-left">Destination</th>
                  <th className="px-2 py-2.5 text-left w-[156px]">Résultat</th>
                  <th className="px-5 py-2.5 text-right w-[148px]">Date</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <CallRow key={c.id} c={c} open={open === c.id} onToggle={() => toggle(c.id)} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Cartes en dessous de lg */}
          <div className="flex flex-col gap-2.5 lg:hidden">
            {calls.map((c) => (
              <CallCard key={c.id} c={c} open={open === c.id} onToggle={() => toggle(c.id)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CallerPill({ c }) {
  const meta = CALLER_META[c.caller_type];
  return (
    <span className={cn(PILL, c.blocked ? "bg-rose-100 text-rose-700" : meta?.cls)}>
      <span className={cn(DOT, c.blocked ? "bg-rose-500" : meta?.dot)} />
      {c.blocked ? "Bloqué" : meta?.label || c.caller_type}
    </span>
  );
}

function ResultPill({ result }) {
  const meta = RESULT_META[result];
  return (
    <span className={cn(PILL, meta?.cls || "bg-slate-100 text-slate-600")}>
      <span className={cn(DOT, meta?.dot || "bg-slate-400")} />
      {meta?.label || result}
    </span>
  );
}

function NumberCell({ c, open }) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <ChevronRight size={14} className={cn("shrink-0 text-slate-300 transition-transform", open && "rotate-90")} />
      <span className="font-mono text-[13px] font-medium text-slate-800 truncate">{c.phone_number}</span>
      {c.watched && <Eye size={13} className="shrink-0 text-sky-500" />}
      {c.anomaly && <AlertTriangle size={13} className="shrink-0 text-amber-500" />}
    </div>
  );
}

function CallRow({ c, open, onToggle }) {
  return (
    <>
      <tr data-testid={`call-row-${c.id}`} onClick={onToggle} className={cn(TR, "cursor-pointer", open && "bg-slate-50/70")}>
        <td className="px-5 py-2.5">
          <NumberCell c={c} open={open} />
        </td>
        <td className="px-2 py-2.5"><CallerPill c={c} /></td>
        <td className="px-2 py-2.5 text-slate-600 truncate">{c.company_name || "—"}</td>
        <td className="px-2 py-2.5 text-slate-500 text-[13.5px] truncate">{c.final_destination || c.initial_destination || "—"}</td>
        <td className="px-2 py-2.5"><ResultPill result={c.result} /></td>
        <td className="px-5 py-2.5 text-right font-mono text-xs text-slate-400 whitespace-nowrap">{fmtDate(c.created_at)}</td>
      </tr>
      {open && (
        <tr className="border-b border-slate-50">
          <td colSpan={6} className="p-0">
            <Details c={c} />
          </td>
        </tr>
      )}
    </>
  );
}

function CallCard({ c, open, onToggle }) {
  return (
    <div data-testid={`call-row-${c.id}`} className={cn(CARD, "overflow-hidden")}>
      <button onClick={onToggle} className="w-full text-left p-4">
        <div className="flex items-center justify-between gap-3">
          <NumberCell c={c} open={open} />
          <span className="font-mono text-[11.5px] text-slate-400 whitespace-nowrap">{fmtDate(c.created_at)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <CallerPill c={c} />
          <ResultPill result={c.result} />
        </div>
        <div className="mt-3 space-y-1 text-sm">
          <div className="flex justify-between gap-3">
            <span className="text-xs text-slate-400">Entreprise</span>
            <span className="text-slate-600 truncate">{c.company_name || "—"}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-xs text-slate-400">Destination</span>
            <span className="text-slate-600 truncate">{c.final_destination || c.initial_destination || "—"}</span>
          </div>
        </div>
      </button>
      {open && <Details c={c} />}
    </div>
  );
}

function Details({ c }) {
  return (
    <div className="bg-slate-50/60 border-t border-slate-100 px-5 py-4 grid gap-5 md:grid-cols-2">
      <div>
        <div className="font-mono text-[10.5px] uppercase tracking-wider text-slate-400 mb-2">Décision de routage</div>
        <ol className="space-y-1.5">
          {(c.decision_trace || []).map((t, i) => (
            <li key={i} className="flex gap-2.5 text-[13px] text-slate-600">
              <span className="font-mono text-indigo-500 shrink-0">{i + 1}.</span>
              <span className="text-pretty">{t}</span>
            </li>
          ))}
          {(c.decision_trace || []).length === 0 && <li className="text-[13px] text-slate-400">Aucune trace</li>}
        </ol>
      </div>
      <div>
        <div className="font-mono text-[10.5px] uppercase tracking-wider text-slate-400 mb-2">Cascade prévue</div>
        <ol className="space-y-1.5">
          {(c.steps || []).map((s, i) => (
            <li key={i} className="flex items-baseline gap-2.5 text-[13px] text-slate-600">
              <span className="font-mono text-slate-300 shrink-0">{i + 1}.</span>
              <span className="flex-1 min-w-0 truncate">
                {s.label}{s.user_name ? ` — ${s.user_name}` : s.number ? ` — ${s.number}` : ""}
              </span>
              <span className="font-mono text-[11.5px] text-slate-400 shrink-0">{s.ring_seconds ? `${s.ring_seconds}s` : "—"}</span>
            </li>
          ))}
          {(c.steps || []).length === 0 && <li className="text-[13px] text-slate-400">Aucune étape</li>}
        </ol>
        <div className="mt-3 text-xs text-slate-400">{summaryOf(c)}</div>
      </div>
    </div>
  );
}
